import React, { useState, useEffect } from 'react';
import { Clock, User, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../lib/api';
import { cn } from '../../lib/utils';

const ACTION_LABELS = {
  candidate_applied: 'Candidat ajouté au pipeline',
  candidate_preselected: 'Candidat présélectionné',
  candidate_shortlisted: 'Candidat shortlisté',
  candidate_presented_to_client: 'Candidat présenté au client',
  candidate_client_interested: 'Client intéressé',
  candidate_client_not_interested: 'Client non intéressé',
  candidate_interview_done: 'Entretien réalisé',
  candidate_hired: 'Candidat embauché',
  candidate_rejected_after_interview: 'Refusé après entretien',
  candidate_rejected: 'Candidat rejeté',
  candidate_removed: 'Candidat retiré du pipeline',
  candidate_proposed: 'Candidat proposé',
  candidate_interview: 'Candidat en entretien',
  client_decision: 'Décision client',
  client_link_generated: 'Lien client généré',
  client_link_revoked: 'Lien client révoqué',
  mission_created: 'Mission créée',
  mission_updated: 'Mission modifiée',
  mission_staffed: 'Mission staffée',
  candidate_kept: 'Candidat gardé',
  candidate_passed: 'Candidat passé',
  candidate_status_changed: 'Statut modifié',
  candidate_qualified: 'Candidat qualifié',
  email_sent: 'Email envoyé',
};

const STATUS_LABELS = {
  applied: 'Candidature',
  preselected: 'Présélectionné',
  shortlisted: 'Shortlisté',
  presented_to_client: 'Présenté',
  client_interested: 'Client intéressé',
  client_not_interested: 'Client non intéressé',
  interview_done: 'Entretien réalisé',
  hired: 'Embauché',
  rejected_after_interview: 'Refusé après entretien',
  rejected: 'Rejeté',
  proposed: 'Proposé',
  interview: 'Entretien',
};

const getActionColor = (actionType) => {
  if (actionType?.includes('hired')) return 'bg-green-500';
  if (actionType?.includes('rejected') || actionType?.includes('not_interested')) return 'bg-red-400';
  if (actionType?.includes('interview')) return 'bg-blue-500';
  if (actionType?.includes('presented') || actionType?.includes('proposed')) return 'bg-purple-500';
  if (actionType?.includes('interested')) return 'bg-indigo-500';
  if (actionType?.includes('shortlisted')) return 'bg-orange-400';
  if (actionType?.includes('preselected')) return 'bg-amber-400';
  if (actionType?.includes('decision')) return 'bg-indigo-500';
  return 'bg-gray-400';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 1) return "À l'instant";
  if (mins < 60) return `Il y a ${mins}min`;
  if (hours < 24) return `Il y a ${hours}h`;
  if (days < 7) return `Il y a ${days}j`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const TimelineActivity = ({ missionId, maxItems = 15 }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (missionId) fetchLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/action-logs/mission/${missionId}?limit=${maxItems}`);
      const data = await res.json();
      setLogs(data.logs || []);
    } catch (e) {
      console.error('Failed to fetch logs:', e);
    } finally {
      setLoading(false);
    }
  };

  const getActionLabel = (log) => {
    const base = ACTION_LABELS[log.action_type] || log.action_type;
    if (log.candidate_name) return `${base} — ${log.candidate_name}`;
    return base;
  };

  const getTransition = (log) => {
    if (!log.old_value && !log.new_value) return null;
    const from = STATUS_LABELS[log.old_value] || log.old_value;
    const to = STATUS_LABELS[log.new_value] || log.new_value;
    if (from && to) return `${from} → ${to}`;
    if (to) return to;
    return null;
  };

  if (loading) {
    return (
      <div className="mt-6" data-testid="timeline-loading">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-500">Chargement de l'historique...</span>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={`skeleton-${i}`} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="timeline-activity">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-4 w-full text-left"
        data-testid="timeline-toggle"
      >
        <Clock className="h-4 w-4 text-tp-primary" />
        <span className="text-sm font-semibold text-tp-primary">
          Historique d'activité
        </span>
        <span className="text-xs text-gray-400 ml-1">({logs.length})</span>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400 ml-auto" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 ml-auto" />
        )}
      </button>

      {expanded && (
        <div className="relative">
          {logs.length === 0 ? (
            <p className="text-sm text-gray-400 italic pl-6" data-testid="timeline-empty">
              Aucune activité enregistrée pour cette mission.
            </p>
          ) : (
            <div className="space-y-0">
              {logs.map((log, idx) => {
                const transition = getTransition(log);
                return (
                  <div
                    key={log.id || idx}
                    className="flex items-start gap-3 py-2.5 relative"
                    data-testid={`timeline-entry-${idx}`}
                  >
                    {/* Dot + Line */}
                    <div className="flex flex-col items-center pt-1.5">
                      <div className={cn("w-2.5 h-2.5 rounded-full flex-shrink-0", getActionColor(log.action_type))} />
                      {idx < logs.length - 1 && (
                        <div className="w-px flex-1 bg-gray-200 mt-1 min-h-[20px]" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 -mt-0.5">
                      <p className="text-sm text-gray-800 leading-tight">
                        {getActionLabel(log)}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {transition && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">
                            <ArrowRight className="h-3 w-3" />
                            {transition}
                          </span>
                        )}
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {log.performed_by_name || 'Système'}
                        </span>
                      </div>
                    </div>

                    {/* Timestamp */}
                    <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0 pt-0.5">
                      {formatDate(log.performed_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimelineActivity;
